import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Alert } from "../../components/Alert";
import { Pagination } from "../../components/crm/Pagination";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { StatusBadge } from "../../components/crm/StatusBadge";
import { Spinner } from "../../components/Spinner";
import { isApiError, listCompanies, listContacts } from "../../lib/crm-api";
import type { Company, Contact, ContactListParams } from "../../lib/crm-types";
import { formatDate } from "../../lib/format";
import { useConfig } from "../../state/config-context";
import { useSession } from "../../state/session-context";

interface Filters {
  q: string;
  status: "" | "active" | "inactive" | "archived";
  company_id: string;
}

const EMPTY_FILTERS: Filters = { q: "", status: "", company_id: "" };
const PAGE_LIMIT = 25;

function contactName(contact: Contact): string {
  return [contact.first_name, contact.last_name].filter((part) => Boolean(part)).join(" ");
}

function ContactsInner(): ReactNode {
  const { t, lang } = useConfig();
  const { hasPermission } = useSession();
  const canCreate = hasPermission("crm.contact.create");
  const canReadCompanies = hasPermission("crm.company.read");

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [search, setSearch] = useState("");
  const [companyOptions, setCompanyOptions] = useState<Company[]>([]);
  const [items, setItems] = useState<Contact[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canReadCompanies) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const page = await listCompanies({ limit: 100 });
        if (!cancelled) {
          setCompanyOptions(page.items);
        }
      } catch {
        // The company filter is optional; a failure there must not break the list.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canReadCompanies]);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const params: ContactListParams = { limit: PAGE_LIMIT, cursor };
      if (filters.q !== "") params.q = filters.q;
      if (filters.status !== "") params.status = filters.status;
      if (filters.company_id !== "") params.company_id = filters.company_id;
      const page = await listContacts(params);
      setItems(page.items);
      setNextCursor(page.next_cursor);
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  }, [cursor, filters, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const onFilter = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setCursor(undefined);
    setCursorStack([]);
    setFilters((current) => ({ ...current, q: search.trim() }));
  };

  const onClear = (): void => {
    setSearch("");
    setCursor(undefined);
    setCursorStack([]);
    setFilters(EMPTY_FILTERS);
  };

  const locale = lang === "es" ? "es-ES" : "en-US";
  const companyName = (companyId: string | null): string => {
    if (companyId === null) return "—";
    return companyOptions.find((company) => company.id === companyId)?.name ?? "—";
  };

  return (
    <section className="page" aria-labelledby="crm-contacts-title">
      <header className="page__header page__header--row">
        <div>
          <h1 id="crm-contacts-title">{t("crm.contacts.title")}</h1>
          <p className="muted">{t("crm.contacts.subtitle")}</p>
        </div>
        {canCreate ? (
          <Link className="btn btn--primary" to="/crm/contacts/new">
            {t("crm.contacts.new")}
          </Link>
        ) : null}
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}

      <form className="filters" onSubmit={onFilter}>
        <label className="field" htmlFor="contacts-q">
          <span>{t("crm.common.search")}</span>
          <input
            id="contacts-q"
            value={search}
            placeholder={t("crm.contacts.searchPlaceholder")}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <label className="field" htmlFor="contacts-status">
          <span>{t("crm.common.status")}</span>
          <select
            id="contacts-status"
            value={filters.status}
            onChange={(event) => {
              setCursor(undefined);
              setCursorStack([]);
              setFilters((current) => ({
                ...current,
                status: event.target.value as Filters["status"],
              }));
            }}
          >
            <option value="">{t("crm.common.all")}</option>
            <option value="active">{t("crm.status.active")}</option>
            <option value="inactive">{t("crm.status.inactive")}</option>
            <option value="archived">{t("crm.status.archived")}</option>
          </select>
        </label>
        {canReadCompanies ? (
          <label className="field" htmlFor="contacts-company">
            <span>{t("crm.contact.company")}</span>
            <select
              id="contacts-company"
              value={filters.company_id}
              onChange={(event) => {
                setCursor(undefined);
                setCursorStack([]);
                setFilters((current) => ({ ...current, company_id: event.target.value }));
              }}
            >
              <option value="">{t("crm.common.all")}</option>
              {companyOptions.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <div className="filters__actions">
          <button type="submit" className="btn btn--primary">
            {t("crm.common.apply")}
          </button>
          <button type="button" className="btn btn--ghost" onClick={onClear}>
            {t("crm.common.clear")}
          </button>
        </div>
      </form>

      {loading ? <Spinner label={t("app.loading")} /> : null}

      {!loading && items.length === 0 ? (
        <p className="empty">{t("crm.contacts.empty")}</p>
      ) : null}

      {!loading && items.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("crm.contacts.title")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("crm.contact.firstName")}</th>
                <th scope="col">{t("crm.contact.company")}</th>
                <th scope="col">{t("crm.contact.email")}</th>
                <th scope="col">{t("crm.common.status")}</th>
                <th scope="col">{t("crm.common.updated")}</th>
                <th scope="col" className="table__actions-col">
                  <span className="sr-only">{t("crm.common.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((contact) => (
                <tr key={contact.id}>
                  <td>
                    <Link to={`/crm/contacts/${contact.id}`}>{contactName(contact)}</Link>
                  </td>
                  <td>{companyName(contact.company_id)}</td>
                  <td>{contact.email ?? "—"}</td>
                  <td>
                    <StatusBadge status={contact.status} />
                  </td>
                  <td>{formatDate(contact.updated_at, locale)}</td>
                  <td className="table__actions-col">
                    <Link className="btn btn--ghost btn--sm" to={`/crm/contacts/${contact.id}`}>
                      {t("crm.common.view")}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <Pagination
        hasPrev={cursorStack.length > 0}
        hasNext={nextCursor !== null}
        busy={loading}
        onPrev={() => {
          if (cursorStack.length === 0) {
            return;
          }
          const previous = cursorStack[cursorStack.length - 1];
          setCursorStack((stack) => stack.slice(0, -1));
          setCursor(previous);
        }}
        onNext={() => {
          if (nextCursor === null) return;
          setCursorStack((stack) => [...stack, cursor]);
          setCursor(nextCursor);
        }}
      />
    </section>
  );
}

export function ContactsPage(): ReactNode {
  return (
    <RequirePermission permission="crm.contact.read">
      <ContactsInner />
    </RequirePermission>
  );
}
